import { PlanOption } from '../plan-selection/types'

export const AD_SPEND_STEPS = [5, 10, 15, 20, 30, 40, 50, 75, 100] as const

export const CUSTOM_PLAN_THRESHOLD = 100

export const PLAN_SPEND_LIMITS: Record<string, number> = {
	lite: 5,
	standard: 50,
	pro: 100,
	custom: Infinity
}

export const planOptions: PlanOption[] = [
	{
		id: 'lite',
		name: 'Lite',
		description:
			'Essential protection for common invalid click fraud threats.',
		features: [
			{ label: 'Protection for Meta, Microsoft and Google Ads', available: true },
			{ label: 'Single website', available: true },
			{ label: 'Single PPC account', available: true },
			{ label: 'Unlimited clicks', available: true },
			{ label: 'Dedicated Customer Support Manager', available: false, requiredPlan: 'Custom' }
		]
	},
	{
		id: 'standard',
		name: 'Standard',
		description:
			'Advanced protection to safeguard against dynamic threats.',
		features: [
			{ label: 'Includes all features from **Lite** plan', available: true },
			{ label: 'Protection for Meta, Microsoft and Google Ads', available: true },
			{ label: 'Up to 3 websites', available: true },
			{ label: 'Up to 2 PPC accounts/website', available: true },
			{ label: 'Unlimited clicks', available: true },
			{ label: 'Dedicated Customer Support Manager', available: false, requiredPlan: 'Custom' }
		]
	},
	{
		id: 'pro',
		name: 'Pro',
		description:
			'Full-stack highly customizable + AI-enabled protection for high-volume traffic.',
		features: [
			{ label: 'Includes everything from **Standard** plan', available: true },
			{ label: 'Protection for Meta, Microsoft and Google Ads', available: true },
			{ label: 'Unlimited websites', available: true },
			{ label: 'Unlimited PPC accounts', available: true },
			{ label: 'Unlimited clicks', available: true },
			{ label: 'Dedicated Customer Support Manager', available: false, requiredPlan: 'Custom' }
		]
	},
	{
		id: 'custom',
		name: 'Custom',
		description:
			'Tailored and fully managed 360-protection based on unique threat profile needs.',
		features: [
			{ label: 'Includes everything from **Pro** plan', available: true },
			{ label: 'Protection for Meta, Microsoft and Google Ads', available: true },
			{ label: 'Unlimited websites', available: true },
			{ label: 'Unlimited PPC accounts', available: true },
			{ label: 'Unlimited clicks', available: true },
			{ label: 'Dedicated Customer Support Manager', available: true }
		]
	}
]

export type PricePill = {
	label: string
	monthly: string
	quarterly: string
	annually: string
}

export type AdSpendValue = {
	text: string
	variant: 'warning' | 'success'
}

export type PlatformLogos = 'all'

export interface FeatureRow {
	name: string
	tooltip?: string
	type?: 'price' | 'adSpend' | 'platforms'
	lite: boolean | string | PricePill | AdSpendValue | PlatformLogos
	standard: boolean | string | PricePill | AdSpendValue | PlatformLogos
	pro: boolean | string | PricePill | AdSpendValue | PlatformLogos
	custom: boolean | string | PricePill | AdSpendValue | PlatformLogos
}

export const featureComparisonData: FeatureRow[] = [
	{
		name: 'Price (USD)',
		type: 'price',
		lite: { label: 'Lite', monthly: '$89', quarterly: '$80', annually: '$74' },
		standard: { label: 'Standard', monthly: '$359', quarterly: '$323', annually: '$287' },
		pro: { label: 'Pro', monthly: '$509', quarterly: '$458', annually: '$407' },
		custom: { label: 'Custom', monthly: 'Custom quote', quarterly: 'Custom quote', annually: 'Custom quote' }
	},
	{
		name: 'Monthly ad spend protection',
		type: 'adSpend',
		tooltip: 'Total monthly ad spend across all ad accounts you connect to your workspace.',
		lite: { text: 'Up to $5,000 ad spend', variant: 'warning' },
		standard: { text: 'Up to $50,000 ad spend', variant: 'success' },
		pro: { text: 'Up to $100,000 ad spend', variant: 'success' },
		custom: { text: 'Custom ad spend', variant: 'success' }
	},
	{
		name: 'Protection for',
		type: 'platforms',
		tooltip: undefined,
		lite: 'all',
		standard: 'all',
		pro: 'all',
		custom: 'all'
	},
	{
		name: 'Websites protected',
		tooltip: undefined,
		lite: 'Single website',
		standard: 'Up to 3 websites',
		pro: 'Unlimited',
		custom: 'Unlimited'
	},
	{
		name: 'PPC accounts',
		tooltip: undefined,
		lite: 'Single PPC account',
		standard: 'Two PPC accounts per website',
		pro: 'Unlimited',
		custom: 'Unlimited'
	},
	{
		name: 'Scheduled reports',
		tooltip: 'Stay on top of click fraud with automated reports. Get up-to-date insights on patterns and protection metrics at the website, campaign, placement, or keyword level.',
		lite: true,
		standard: true,
		pro: true,
		custom: true
	},
	{
		name: 'Custom Rule Engine',
		tooltip: 'Custom Rules offer precise control to define specific conditions for detecting and blocking suspicious activity.',
		lite: false,
		standard: true,
		pro: true,
		custom: true
	},
	{
		name: 'Blacklist management',
		tooltip: 'Build your own protection layer with no limits. Unlike ad platforms that cap IP exclusions, ClickGuard lets you blacklist any fraudulent IP, device, or source — automatically or on demand.',
		lite: false,
		standard: true,
		pro: true,
		custom: true
	},
	{
		name: 'Browser extension',
		tooltip: 'See what ClickGuard is identifying and blocking, right inside Google Ads. Monitor traffic quality and act on threats without leaving your campaign view.',
		lite: false,
		standard: true,
		pro: true,
		custom: true
	},
	{
		name: 'Conversion-based exclusions',
		tooltip: 'By enabling ClickGuard conversion tracking on your website, you can block visitors who repeatedly click your ads without converting, or exclude them after they convert. This is an optional setting.',
		lite: false,
		standard: false,
		pro: true,
		custom: true
	},
	{
		name: 'Server-side tracking',
		tooltip: 'Advanced ClickGuard tracking method designed to improve click and visitor tracking accuracy by bypassing browser-level restrictions and ad blockers.',
		lite: false,
		standard: false,
		pro: true,
		custom: true
	},
	{
		name: 'Personal team onboarding',
		tooltip: 'We\'ll walk you through setup, help fine-tune protection rules, and make sure you\'re confident using the platform from day one.',
		lite: false,
		standard: false,
		pro: true,
		custom: true
	},
	{
		name: 'Dedicated Customer Success Manager',
		tooltip: 'Your CSM helps you onboard faster, optimize how you use the platform, proactively solve issues, and identify opportunities to get more value from your investment.',
		lite: false,
		standard: false,
		pro: false,
		custom: true
	}
]


